import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Field, FieldProps, Form, Formik } from 'formik';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Mail } from '@mui/icons-material';

import { useSnackbar } from '../../snackbar/SnackbarContext';

import { Providers } from '../providers/Providers';
import { SubmitButton } from '../../_shared/form/SubmitButton';
import { useAuth } from '../AuthenticationContext';
import { useGameTheme } from '../../GameThemeContext';
import { RoutePrams } from '../Authentication.model';

interface FormValues {
  email: string;
}

export function Signup(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  const snackbar = useSnackbar();
  const theme = useGameTheme();
  const params = useParams<RoutePrams>();

  const initialValuesFormik: FormValues = {
    email: '',
  };
  const SignupSchema = Yup.object().shape({
    email: Yup.string()
      .email('form.mail-invalid')
      .required('form.field-required'),
  });

  const handleSubmitForm = async (values: FormValues): Promise<void> => {
    const { available } = await auth.checkPlayerAvailability(values.email);

    if (!available) {
      snackbar.addErrorMessage('PlayerEmailAlreadyExistsError', t('errors.emailAlreadyExists'));
    } else {
      history.push(params?.gameId
        ? `/register/${params.gameId}` : '/register', {
        email: values.email,
      });
    }
  };

  useEffect(() => {
    if (params.gameId) {
      theme.setGameId(params.gameId);
    }
  }, [params]);

  return (
    <div className='auth-card-container card-bhvra'>
      <div className='row text-center'>
        <div className='col-12'>
          <h2 className='my-3'>{t('account.welcome')}</h2>
          <p className='my-3 font-weight-light'>
            {t('auth.register.sign-up-description')}
          </p>
        </div>

        <Providers textBtn='auth.register.sign-up-with' />
      </div>

      <hr className='hr-text' data-content={t('or')} />

      <Formik
        initialValues={initialValuesFormik}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={SignupSchema}
        onSubmit={handleSubmitForm}
      >
        {({ errors, touched, handleSubmit }) => (
          <Form className='my-4 form-light'>
            <Field name='email'>
              {({ field }: FieldProps) => (
                <div className='form-group'>
                  <label htmlFor='email'>{t('form.label.email')}</label>
                  <div className='input-group'>
                    <div className='input-group-prepend'>
                      <span className='input-group-text'>
                        <Mail className='icon' />
                      </span>
                    </div>
                    <input
                      name='email'
                      id='email'
                      data-cy='email-input'
                      placeholder={t('form.label.email')}
                      className={`form-control
                            ${(errors.email && touched.email && handleSubmit) ? 'is-invalid' : ''}`}
                      type='email'
                      {...field}
                    />
                  </div>
                  {errors.email && touched.email && handleSubmit ? (
                    <div className='form-error' data-cy='email-input-error-text'>{t(`${errors.email}`)}</div>
                  ) : null}
                </div>
              )}
            </Field>
            <SubmitButton name='continue' containerClassName='my-5' />
            <div className='float-center'>
              <p className='text-center'>
                {t('auth.register.have-account')}
                <Link
                  className='font-weight-bold text-nowrap'
                  to={params?.gameId
                    ? `/auth/signin/${params.gameId}` : '/auth/signin'}
                >
                  <u>{t('auth.register.sign-in-page')}</u>
                </Link>
              </p>
            </div>

          </Form>
        )}
      </Formik>
    </div>
  );
}
